import { useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Check, Copy } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, FieldError } from '@/components/ui/input';
import { Select, SelectItem } from '@/components/ui/select';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { useLocaleFormatters } from '@/lib/format';
import { apiErrorMessage } from '@/lib/api-errors';
import { ApiError } from '@wrapforce/shared';
import {
  useTwilioMessagingProfile,
  useSaveTwilioBusinessInfo,
  useSubmitTwilioOnboarding,
  useResendTwilioOtp,
  type TwilioBusinessInfo,
  type TwilioBusinessInfoInput,
  type TwilioBusinessType,
  type TwilioMessagingProfile,
  type TwilioOnboardingStatus,
} from './twilio-api';
import { invalidFieldsFromApiError } from './form-errors';

const PROGRESS_STAGES: { key: 'profile' | 'brand' | 'campaign' | 'number'; statuses: TwilioOnboardingStatus[] }[] = [
  { key: 'profile', statuses: ['profile_pending', 'profile_approved'] },
  { key: 'brand', statuses: ['brand_pending', 'brand_otp_pending', 'brand_approved'] },
  { key: 'campaign', statuses: ['campaign_pending', 'campaign_approved'] },
  { key: 'number', statuses: ['number_provisioning'] },
];

const STAGE_ORDER: TwilioOnboardingStatus[] = [
  'profile_pending',
  'profile_approved',
  'brand_pending',
  'brand_otp_pending',
  'brand_approved',
  'campaign_pending',
  'campaign_approved',
  'number_provisioning',
];

function normalizeUsCaPhone(input: string): string | null {
  const digits = input.replace(/\D/g, '');
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (local.length !== 10) return null;
  return `+1${local}`;
}

function formatE164(e164: string): string {
  const digits = e164.replace(/^\+1/, '');
  if (digits.length !== 10) return e164;
  return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function MessagingPhoneCard({ workspaceId }: { workspaceId: string | null }) {
  const { t } = useTranslation('integrations');
  const { data, isLoading } = useTwilioMessagingProfile(workspaceId);
  const [showForm, setShowForm] = useState(false);

  const profile = data?.profile;
  const status = profile?.onboardingStatus;

  let view: 'shared' | 'form' | 'progress' | 'failed' | 'active' | 'suspended' = 'shared';
  if (profile) {
    if (status === 'active') view = 'active';
    else if (status === 'suspended') view = 'suspended';
    else if (showForm || status === 'collecting_business_info') view = 'form';
    else if (status === 'failed') view = 'failed';
    else if (STAGE_ORDER.includes(status as TwilioOnboardingStatus)) view = 'progress';
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('messagingPhone.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {isLoading || !profile ? (
          <p className="text-sm text-muted">{t('common:actions.loading')}</p>
        ) : view === 'suspended' ? (
          <SuspendedBanner />
        ) : view === 'active' ? (
          <ActiveNumber phoneNumber={profile.phoneNumber!} />
        ) : view === 'form' ? (
          <BusinessInfoForm workspaceId={workspaceId} businessInfo={data?.businessInfo ?? null} onSubmitted={() => setShowForm(false)} />
        ) : view === 'failed' ? (
          <FailedBanner failureReason={profile.failureReason} onEditAndResubmit={() => setShowForm(true)} />
        ) : view === 'progress' ? (
          <>
            {status === 'brand_otp_pending' ? (
              <OtpPendingBanner profile={profile} businessInfo={data?.businessInfo ?? null} workspaceId={workspaceId} />
            ) : null}
            <ProgressTracker profile={profile} />
          </>
        ) : (
          <SharedTierState onGetNumber={() => setShowForm(true)} />
        )}
      </CardContent>
    </Card>
  );
}

function SharedTierState({ onGetNumber }: { onGetNumber: () => void }) {
  const { t } = useTranslation('integrations');
  return (
    <div className="flex flex-col gap-3">
      <div>
        <div className="text-sm font-semibold text-ink">{t('messagingPhone.shared.headline')}</div>
        <p className="mt-1 text-xs text-muted">{t('messagingPhone.shared.description')}</p>
      </div>
      <ul className="flex flex-col gap-1 text-xs text-muted">
        <li>- {t('messagingPhone.shared.missing1')}</li>
        <li>- {t('messagingPhone.shared.missing2')}</li>
        <li>- {t('messagingPhone.shared.missing3')}</li>
        <li>- {t('messagingPhone.shared.missing4')}</li>
      </ul>
      <Button onClick={onGetNumber} className="self-start">
        {t('messagingPhone.shared.cta')}
      </Button>
    </div>
  );
}

const BUSINESS_TYPE_KEYS: Record<TwilioBusinessType, string> = {
  sole_proprietorship: 'messagingPhone.form.businessTypeSoleProprietorship',
  partnership: 'messagingPhone.form.businessTypePartnership',
  llc: 'messagingPhone.form.businessTypeLlc',
  corporation: 'messagingPhone.form.businessTypeCorporation',
  co_operative: 'messagingPhone.form.businessTypeCoOperative',
  non_profit: 'messagingPhone.form.businessTypeNonProfit',
};

function emptyFormState(info: TwilioBusinessInfo | null): TwilioBusinessInfoInput {
  return {
    legalName: info?.legalName ?? '',
    ein: '',
    businessType: info?.businessType ?? 'llc',
    addressStreet: info?.addressStreet ?? '',
    addressCity: info?.addressCity ?? '',
    addressRegion: info?.addressRegion ?? '',
    addressPostalCode: info?.addressPostalCode ?? '',
    addressCountry: info?.addressCountry ?? 'US',
    websiteUrl: info?.websiteUrl ?? '',
    repName: info?.repName ?? '',
    repEmail: info?.repEmail ?? '',
    repPhone: info?.repPhone ?? '',
    repTitle: info?.repTitle ?? '',
  };
}

function BusinessInfoForm({
  workspaceId,
  businessInfo,
  onSubmitted,
}: {
  workspaceId: string | null;
  businessInfo: TwilioBusinessInfo | null;
  onSubmitted: () => void;
}) {
  const { t } = useTranslation('integrations');
  const [form, setForm] = useState<TwilioBusinessInfoInput>(() => emptyFormState(businessInfo));
  const [saved, setSaved] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof TwilioBusinessInfoInput, string>>>({});
  const save = useSaveTwilioBusinessInfo(workspaceId);
  const submit = useSubmitTwilioOnboarding(workspaceId);
  const { confirm, ConfirmDialog } = useConfirm();

  function update<K extends keyof TwilioBusinessInfoInput>(key: K, value: TwilioBusinessInfoInput[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  async function handleSave() {
    const nextErrors: Partial<Record<keyof TwilioBusinessInfoInput, string>> = {};
    if (form.legalName.trim().length < 2) nextErrors.legalName = t('errors.legalNameMin');
    const ein = (form.ein ?? '').trim();
    if (ein && !/^\d{2}-?\d{7}$/.test(ein)) {
      nextErrors.ein = t('errors.einInvalid');
    } else if (form.businessType === 'sole_proprietorship' && ein) {
      nextErrors.ein = t('errors.einForbiddenSoleProp');
    } else if (form.businessType !== 'sole_proprietorship' && !ein && !businessInfo?.ein) {
      nextErrors.ein = t('errors.einRequired');
    }
    if (!form.addressStreet.trim()) nextErrors.addressStreet = t('errors.required');
    if (!form.addressCity.trim()) nextErrors.addressCity = t('errors.required');
    if (!form.addressRegion.trim()) nextErrors.addressRegion = t('errors.required');
    if (!form.addressPostalCode.trim()) nextErrors.addressPostalCode = t('errors.required');
    let websiteValid = false;
    try {
      websiteValid = new URL(form.websiteUrl).protocol === 'https:';
    } catch {
      websiteValid = false;
    }
    if (!websiteValid) nextErrors.websiteUrl = t('errors.websiteInvalid');
    if (!form.repName.trim()) nextErrors.repName = t('errors.required');
    if (!/^\S+@\S+\.\S+$/.test(form.repEmail.trim())) nextErrors.repEmail = t('errors.emailInvalid');
    const repPhone = normalizeUsCaPhone(form.repPhone);
    if (!repPhone) nextErrors.repPhone = t('errors.phoneInvalid');
    if (!form.repTitle.trim()) nextErrors.repTitle = t('errors.required');

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    try {
      const payload: TwilioBusinessInfoInput = { ...form, repPhone: repPhone! };
      if (!payload.ein) delete payload.ein;
      await save.mutateAsync(payload);
      setSaved(true);
      toast.success(t('toast.businessInfoSaved'));
    } catch (err) {
      const fields = invalidFieldsFromApiError(err);
      const mapped: Partial<Record<keyof TwilioBusinessInfoInput, string>> = {};
      for (const field of fields) {
        if (field in form) {
          mapped[field as keyof TwilioBusinessInfoInput] = t('errors.checkField');
        }
      }
      if (Object.keys(mapped).length > 0) {
        setErrors(mapped);
      } else {
        toast.error(t('toast.businessInfoSaveFailed'), { description: apiErrorMessage(err) });
      }
    }
  }

  async function handleSubmit() {
    const confirmed = await confirm({
      title: t('messagingPhone.form.submitConfirmTitle'),
      description: t('messagingPhone.form.submitConfirmDescription'),
      confirmLabel: t('messagingPhone.form.submit'),
    });
    if (!confirmed) return;
    try {
      await submit.mutateAsync();
      toast.success(t('toast.onboardingSubmitted'));
      onSubmitted();
    } catch (err) {
      toast.error(t('toast.onboardingSubmitFailed'), { description: apiErrorMessage(err) });
    }
  }

  const isSoleProp = form.businessType === 'sole_proprietorship';

  return (
    <div className="flex flex-col gap-4">
      <ConfirmDialog />
      <p className="text-xs text-muted">{t('messagingPhone.form.explainer')}</p>
      {isSoleProp ? <p className="text-xs text-muted">{t('messagingPhone.form.solePropExplainer')}</p> : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="legalName">{t('messagingPhone.form.legalName')}</Label>
          <Input id="legalName" value={form.legalName} onChange={(e) => update('legalName', e.target.value)} />
          <FieldError>{errors.legalName}</FieldError>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="businessType">{t('messagingPhone.form.businessType')}</Label>
          <Select value={form.businessType} onValueChange={(v) => update('businessType', v as TwilioBusinessType)}>
            {(Object.keys(BUSINESS_TYPE_KEYS) as TwilioBusinessType[]).map((type) => (
              <SelectItem key={type} value={type}>
                {t(BUSINESS_TYPE_KEYS[type])}
              </SelectItem>
            ))}
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ein">{isSoleProp ? t('messagingPhone.form.einSoleProp') : t('messagingPhone.form.ein')}</Label>
          <Input
            id="ein"
            value={form.ein}
            onChange={(e) => update('ein', e.target.value)}
            placeholder={businessInfo?.ein ?? t('messagingPhone.form.einPlaceholder')}
          />
          <FieldError>{errors.ein}</FieldError>
          {isSoleProp ? <FieldError>{t('messagingPhone.form.einHelperSoleProp')}</FieldError> : null}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="addressCountry">{t('messagingPhone.form.addressCountry')}</Label>
          <Select value={form.addressCountry} onValueChange={(v) => update('addressCountry', v as 'US' | 'CA')}>
            <SelectItem value="US">{t('twilio.unitedStates')}</SelectItem>
            <SelectItem value="CA">{t('twilio.canada')}</SelectItem>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <Label htmlFor="addressStreet">{t('messagingPhone.form.addressStreet')}</Label>
          <Input id="addressStreet" value={form.addressStreet} onChange={(e) => update('addressStreet', e.target.value)} />
          <FieldError>{errors.addressStreet}</FieldError>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="addressCity">{t('messagingPhone.form.addressCity')}</Label>
          <Input id="addressCity" value={form.addressCity} onChange={(e) => update('addressCity', e.target.value)} />
          <FieldError>{errors.addressCity}</FieldError>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="addressRegion">{t('messagingPhone.form.addressRegion')}</Label>
          <Input id="addressRegion" value={form.addressRegion} onChange={(e) => update('addressRegion', e.target.value)} />
          <FieldError>{errors.addressRegion}</FieldError>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="addressPostalCode">{t('messagingPhone.form.addressPostalCode')}</Label>
          <Input id="addressPostalCode" value={form.addressPostalCode} onChange={(e) => update('addressPostalCode', e.target.value)} />
          <FieldError>{errors.addressPostalCode}</FieldError>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="websiteUrl">{t('messagingPhone.form.websiteUrl')}</Label>
          <Input
            id="websiteUrl"
            value={form.websiteUrl}
            onChange={(e) => update('websiteUrl', e.target.value)}
            placeholder={t('messagingPhone.form.websiteUrlPlaceholder')}
          />
          <FieldError>{errors.websiteUrl}</FieldError>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="repName">{t('messagingPhone.form.repName')}</Label>
          <Input id="repName" value={form.repName} onChange={(e) => update('repName', e.target.value)} />
          <FieldError>{errors.repName}</FieldError>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="repTitle">{t('messagingPhone.form.repTitle')}</Label>
          <Input id="repTitle" value={form.repTitle} onChange={(e) => update('repTitle', e.target.value)} />
          <FieldError>{errors.repTitle}</FieldError>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="repEmail">{t('messagingPhone.form.repEmail')}</Label>
          <Input id="repEmail" type="email" value={form.repEmail} onChange={(e) => update('repEmail', e.target.value)} />
          <FieldError>{errors.repEmail}</FieldError>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="repPhone">{isSoleProp ? t('messagingPhone.form.repPhoneSoleProp') : t('messagingPhone.form.repPhone')}</Label>
          <Input
            id="repPhone"
            value={form.repPhone}
            onChange={(e) => update('repPhone', e.target.value)}
            placeholder={t('messagingPhone.form.repPhonePlaceholder')}
          />
          <FieldError>{errors.repPhone}</FieldError>
          {isSoleProp ? <p className="text-xs text-muted">{t('messagingPhone.form.repPhoneSolePropHelper')}</p> : null}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button variant="outline" loading={save.isPending} onClick={handleSave}>
          {t('messagingPhone.form.save')}
        </Button>
        <Button loading={submit.isPending} disabled={!saved} onClick={handleSubmit}>
          {t('messagingPhone.form.submit')}
        </Button>
      </div>
    </div>
  );
}

function ProgressTracker({ profile }: { profile: { onboardingStatus: TwilioOnboardingStatus; stageTimestamps: Record<string, string> } }) {
  const { t } = useTranslation('integrations');
  const { formatDate } = useLocaleFormatters();
  const currentIndex = STAGE_ORDER.indexOf(profile.onboardingStatus);

  return (
    <div className="flex flex-col gap-4">
      <div className="text-sm font-semibold text-ink">{t('messagingPhone.progress.title')}</div>
      <ol className="flex flex-col gap-3">
        {PROGRESS_STAGES.map((stage) => {
          const lastStatus = stage.statuses[stage.statuses.length - 1]!; // statuses arrays are non-empty constants
          const stageIndex = STAGE_ORDER.indexOf(lastStatus);
          const isComplete = currentIndex > stageIndex || (stage.statuses.length > 1 && currentIndex === stageIndex && profile.onboardingStatus === lastStatus);
          const isCurrent = stage.statuses.includes(profile.onboardingStatus);
          const completedAt = stage.statuses.map((s) => profile.stageTimestamps[s]).find(Boolean);

          return (
            <li key={stage.key} className="flex items-center gap-3">
              <span
                className={
                  isComplete
                    ? 'flex h-6 w-6 items-center justify-center bg-success text-white'
                    : isCurrent
                      ? 'flex h-6 w-6 items-center justify-center border-2 border-accent'
                      : 'flex h-6 w-6 items-center justify-center border border-border'
                }
                aria-hidden
              >
                {isComplete ? (
                  <Check size={14} />
                ) : isCurrent ? (
                  <span className="h-2.5 w-2.5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
                ) : null}
              </span>
              <div>
                <div className={isComplete || isCurrent ? 'text-sm text-ink' : 'text-sm text-muted'}>{t(`messagingPhone.progress.${stage.key}`)}</div>
                {isComplete && completedAt ? (
                  <div className="text-xs text-muted">{t('messagingPhone.progress.completedAt', { date: formatDate(new Date(completedAt), 'PP') })}</div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function ActiveNumber({ phoneNumber }: { phoneNumber: string }) {
  const { t } = useTranslation('integrations');
  const formatted = formatE164(phoneNumber);
  const digits = phoneNumber.replace(/^\+1/, '');
  const domesticFormatted = digits.length === 10 ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}` : phoneNumber;
  const snippet = `<a href="tel:${phoneNumber}">Call or text us: ${domesticFormatted}</a>`;

  function copyNumber() {
    navigator.clipboard.writeText(phoneNumber);
    toast.success(t('toast.numberCopied'));
  }

  function copySnippet() {
    navigator.clipboard.writeText(snippet);
    toast.success(t('toast.snippetCopied'));
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Label>{t('messagingPhone.active.yourNumber')}</Label>
        <div className="mt-1 flex items-center gap-2">
          <span className="font-mono text-lg font-bold text-ink">{formatted}</span>
          <Button variant="outline" size="sm" onClick={copyNumber}>
            <Copy size={13} />
            {t('messagingPhone.active.copy')}
          </Button>
        </div>
        <p className="mt-1 text-xs text-muted">{t('messagingPhone.active.leadNote')}</p>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label>{t('messagingPhone.active.websiteSnippet')}</Label>
        <div className="flex items-center justify-between gap-2 bg-surface-2 px-3 py-2">
          <code className="overflow-x-auto text-xs text-ink">{snippet}</code>
          <Button variant="outline" size="sm" onClick={copySnippet}>
            <Copy size={13} />
            {t('messagingPhone.active.copy')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function FailedBanner({ failureReason, onEditAndResubmit }: { failureReason: string | null; onEditAndResubmit: () => void }) {
  const { t } = useTranslation('integrations');
  return (
    <div className="flex flex-col gap-3">
      <div className="border border-danger bg-danger/10 p-3">
        <div className="text-sm font-semibold text-danger">{t('messagingPhone.progress.failedTitle')}</div>
        <p className="mt-1 text-xs text-muted">{t('messagingPhone.progress.failedExplainer')}</p>
        {failureReason ? (
          <details className="mt-2">
            <summary className="cursor-pointer text-xs text-muted">{t('messagingPhone.progress.technicalDetails')}</summary>
            <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all text-xs text-ink">{failureReason}</pre>
          </details>
        ) : null}
      </div>
      <Button onClick={onEditAndResubmit} className="self-start">
        {t('messagingPhone.form.editAndResubmit')}
      </Button>
    </div>
  );
}

function OtpPendingBanner({
  profile,
  businessInfo,
  workspaceId,
}: {
  profile: TwilioMessagingProfile;
  businessInfo: TwilioBusinessInfo | null;
  workspaceId: string | null;
}) {
  const { t } = useTranslation('integrations');
  const { formatDateTime } = useLocaleFormatters();
  const resend = useResendTwilioOtp(workspaceId);

  const phone = businessInfo?.repPhone ? formatE164(businessInfo.repPhone) : '';
  const sentRecently = profile.otpSentAt ? Date.now() - new Date(profile.otpSentAt).getTime() < 10 * 60_000 : false;

  async function handleResend() {
    try {
      await resend.mutateAsync();
      toast.success(t('toast.otpResent'));
    } catch (err) {
      if (err instanceof ApiError && (err.code === 'RATE_LIMITED' || err.code === 'CONFLICT')) {
        toast.error(t('toast.otpResendFailed'), { description: apiErrorMessage(err) });
      } else {
        toast.error(t('toast.otpResendFailed'));
      }
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="border border-warning bg-warning/10 p-3">
        <div className="text-sm font-semibold text-warning">{t('messagingPhone.otp.title')}</div>
        <p className="mt-1 text-xs text-muted">{t('messagingPhone.otp.description', { phone })}</p>
        <p className="mt-1 text-xs text-muted">{t('messagingPhone.otp.sender')}</p>
        <p className="mt-2 text-xs text-muted">{t('messagingPhone.otp.limits')}</p>
        {sentRecently ? (
          <p className="mt-1 text-xs text-muted">
            {t('messagingPhone.otp.sentRecently', { time: formatDateTime(new Date(profile.otpSentAt!)) })}
          </p>
        ) : null}
      </div>
      <Button variant="outline" loading={resend.isPending} disabled={sentRecently} onClick={handleResend} className="self-start">
        {t('messagingPhone.otp.resend')}
      </Button>
    </div>
  );
}

function SuspendedBanner() {
  const { t } = useTranslation('integrations');
  return (
    <div className="border border-warning bg-warning/10 p-3">
      <div className="text-sm font-semibold text-warning">{t('messagingPhone.suspended.title')}</div>
      <p className="mt-1 text-xs text-muted">{t('messagingPhone.suspended.description')}</p>
    </div>
  );
}
